import { useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2 } from 'lucide-react';
import { Botao } from './base';
import { Entrada, Rotulo, Selecao } from './formulario';
import { Confirmar, Modal } from './sobreposicao';
import { useToast } from './toast';
import { del, get, mensagemDeErro, patch, post } from '../nucleo/api';

/**
 * Ajustes de 27/10 (C1, C14): "Filtros salvos" de verdade no painel de filtros, pelas visões salvas do núcleo (`/visoes`, uma
 * tela por página): escolher aplica, criar grava os filtros do painel, renomear e excluir valem para o escolhido.
 */
interface VisaoFiltro {
  id: string;
  nome: string;
  filtros: Record<string, unknown>;
}
export function FiltrosSalvos({ tela, filtros, aoAplicar }: { tela: string; filtros: Record<string, string>; aoAplicar: (f: Record<string, string>) => void }) {
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const id = useId();
  const [salvo, setSalvo] = useState('');
  const [nome, setNome] = useState<{ modo: 'criar' | 'renomear'; texto: string } | null>(null);
  const [excluir, setExcluir] = useState(false);
  const visoes = useQuery({ queryKey: ['visoes', tela], queryFn: () => get<VisaoFiltro[]>('/visoes', { tela }) });
  const recarregar = () => qc.invalidateQueries({ queryKey: ['visoes', tela] });
  const gravar = useMutation({
    mutationFn: async () => {
      if (!nome?.texto.trim()) return;
      if (nome.modo === 'criar') {
        const novo = crypto.randomUUID();
        await post('/visoes', { id: novo, tela, nome: nome.texto.trim(), filtros: Object.fromEntries(Object.entries(filtros).filter(([, v]) => v)) });
        setSalvo(novo);
      } else await patch(`/visoes/${salvo}`, { nome: nome.texto.trim() });
    },
    onSuccess: async () => {
      toast.sucesso(t('crm.aj.filtroSalvo'));
      setNome(null);
      await recarregar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const apagar = useMutation({
    mutationFn: () => del(`/visoes/${salvo}`),
    onSuccess: async () => {
      setSalvo('');
      setExcluir(false);
      await recarregar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <div className="flex flex-col gap-2" data-filtros-salvos="">
      <Rotulo id={id} rotulo={t('crm.aj.filtrosSalvos')}>
        <div className="flex items-center gap-1">
          <Selecao
            id={id}
            value={salvo}
            vazio={t('crm.filtros.nenhum')}
            opcoes={(visoes.data ?? []).map((v) => ({ valor: v.id, rotulo: v.nome }))}
            onChange={(e) => {
              setSalvo(e.target.value);
              const v = visoes.data?.find((x) => x.id === e.target.value);
              if (v) aoAplicar(Object.fromEntries(Object.entries(v.filtros ?? {}).map(([k, x]) => [k, String(x ?? '')])));
            }}
          />
          <Botao variante="fantasma" tamanho="sm" aria-label={t('crm.aj.renomearFiltro')} title={t('crm.aj.renomearFiltro')} disabled={!salvo} icone={<Pencil className="size-4" />} onClick={() => setNome({ modo: 'renomear', texto: visoes.data?.find((x) => x.id === salvo)?.nome ?? '' })} />
          <Botao variante="fantasma" tamanho="sm" aria-label={t('crm.aj.excluirFiltro')} title={t('crm.aj.excluirFiltro')} disabled={!salvo} icone={<Trash2 className="size-4" />} onClick={() => setExcluir(true)} />
        </div>
      </Rotulo>
      <Botao tamanho="sm" className="self-start" data-salvar-filtro="" onClick={() => setNome({ modo: 'criar', texto: '' })}>
        {t('crm.aj.criarFiltro')}
      </Botao>
      <Modal
        aberto={!!nome}
        aoFechar={() => setNome(null)}
        titulo={nome?.modo === 'renomear' ? t('crm.aj.renomearFiltro') : t('crm.aj.criarFiltro')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setNome(null)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" carregando={gravar.isPending} disabled={!nome?.texto.trim()} onClick={() => gravar.mutate()}>
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        <Rotulo id={`${id}-nome`} rotulo={t('crm.aj.nomeFiltro')} obrigatorio>
          <Entrada id={`${id}-nome`} maxLength={100} value={nome?.texto ?? ''} onChange={(e) => setNome((x) => x && { ...x, texto: e.target.value })} />
        </Rotulo>
      </Modal>
      <Confirmar aberto={excluir} aoFechar={() => setExcluir(false)} perigoso titulo={t('crm.aj.excluirFiltro')} texto={visoes.data?.find((x) => x.id === salvo)?.nome ?? ''} carregando={apagar.isPending} aoConfirmar={() => apagar.mutate()} />
    </div>
  );
}
